import { ArrowUpRightIcon } from '@heroicons/react/24/outline';
import { Link } from '@remix-run/react';
import { GQLOrderEventSeverityType, GQLOrderFragment } from '@theprintbar/graphql';
import { CrewRoutes, cn, getFullName, getReadableDate, getReadableTime } from '@theprintbar/shared';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components';

interface ActivityLogProps {
	order: GQLOrderFragment;
	staff?: boolean;
}

export enum ActivityLogEventLinkType {
	OrderItem = 'OrderItem',
}

export function ActivityLog({ order, staff }: ActivityLogProps) {
	const sortEventsDesc = order.events?.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
	return (
		<div className="text-xs lg:text-sm space-y-2">
			<h5 className="uppercase mb-2">Activity Log</h5>
			<Table>
				<TableHead>
					<TableRow>
						<TableHeader>Time</TableHeader>
						<TableHeader>Date</TableHeader>
						<TableHeader>Account</TableHeader>
						<TableHeader centered>Event</TableHeader>
						<TableHeader>Details</TableHeader>
					</TableRow>
				</TableHead>
				<TableBody>
					{sortEventsDesc?.map(event => {
						let eventColour = 'bg-blue-bright';
						switch (event.severity) {
							case GQLOrderEventSeverityType.Update:
								eventColour = 'bg-green';
								break;
							case GQLOrderEventSeverityType.Important:
								eventColour = 'bg-orange';
								break;
							case GQLOrderEventSeverityType.Critical:
								eventColour = 'bg-red';
								break;
						}

						//Pull []() link text out of details for order item events

						const hasLink = staff && event.event.includes(`[${ActivityLogEventLinkType.OrderItem}](`);
						let localEvent = event.event;
						let link: string | undefined = undefined;
						if (hasLink) {
							const regex = new RegExp(`\\[${ActivityLogEventLinkType.OrderItem}\\]\\((.*?)\\)`);
							const match = event.event.match(regex);
							if (match && match[1]) {
								const orderItemId = match[1];
								//Replace text with "Edited Item" and link to order item
								localEvent = 'Edited Item';
								link = CrewRoutes.Orders.View(order.id).LineItem(orderItemId).Landing;
							}
						}

						return (
							<TableRow key={event.id} href={link}>
								<TableCell className="w-16">{getReadableTime(event.createdAt)}</TableCell>
								<TableCell className="w-40">{getReadableDate(event.createdAt)}</TableCell>
								<TableCell>
									{event.user?.email ? (
										<Link className="underline" to={CrewRoutes.Customers.View(event.user.id).Landing}>
											{getFullName(event.user)}
										</Link>
									) : (
										'System'
									)}
								</TableCell>
								<TableCell centered className="w-64">
									<div
										className={cn(
											'text-xs uppercase py-1 px-2 mx-auto border border-blue rounded-full',
											hasLink && 'underline',
											eventColour,
										)}
									>
										{localEvent}
										{hasLink && <ArrowUpRightIcon className="inline-block w-3 h-3 ml-1" />}
									</div>
								</TableCell>
								<TableCell centered className="w-64 overflow-x-auto">
									{event.details || 'No Details'}
								</TableCell>
							</TableRow>
						);
					})}
					{!order.events && (
						<TableRow>
							<TableCell colSpan={3} className="text-lg text-grey">
								No Activity found...
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</div>
	);
}
